import store from '../../store';
import animationSvc from '../animationSvc';
import editorSvc from '../editorSvc';

let editorScrollerElt;
let previewScrollerElt;
let editorFinishTimeoutId;
let previewFinishTimeoutId;
let skipAnimation;
let isScrollEditor;
let isScrollPreview;
let isEditorMoving;
let isPreviewMoving;
let sectionDescList = [];
let savedScrollPosition = null;

let throttleTimeoutId;
let throttleLastTime = 0;

function throttle(func, wait) {
  clearTimeout(throttleTimeoutId);
  const currentTime = Date.now();
  const localWait = (wait + throttleLastTime) - currentTime;
  if (localWait < 1) {
    throttleLastTime = currentTime;
    func();
  } else {
    throttleTimeoutId = setTimeout(() => {
      throttleLastTime = Date.now();
      func();
    }, localWait);
  }
}

const doScrollSync = () => {
  if (!editorScrollerElt || !previewScrollerElt) {
    return;
  }
  const localSkipAnimation = skipAnimation || !store.getters['layout/styles'].showSidePreview;
  skipAnimation = false;
  if (!store.getters['data/layoutSettings'].scrollSync || sectionDescList.length === 0) {
    return;
  }
  let editorScrollTop = editorScrollerElt.scrollTop;
  if (editorScrollTop < 0) {
    editorScrollTop = 0;
  }
  const previewScrollTop = previewScrollerElt.scrollTop;
  let scrollTo;
  if (isScrollEditor) {
    // Scroll the preview
    isScrollEditor = false;
    sectionDescList.some((sectionDesc) => {
      if (editorScrollTop > sectionDesc.editorDimension.endOffset) {
        return false;
      }
      const posInSection = (editorScrollTop - sectionDesc.editorDimension.startOffset)
        / (sectionDesc.editorDimension.height || 1);
      scrollTo = (sectionDesc.previewDimension.startOffset
        + (sectionDesc.previewDimension.height * posInSection));
      return true;
    });
    // If scrollTo is undefined (scrolled past all sections), scroll to bottom
    const maxPreviewScroll = previewScrollerElt.scrollHeight - previewScrollerElt.offsetHeight;
    if (scrollTo === undefined) {
      scrollTo = maxPreviewScroll;
    } else {
      scrollTo = Math.min(scrollTo, maxPreviewScroll);
    }

    throttle(() => {
      clearTimeout(previewFinishTimeoutId);
      animationSvc.animate(previewScrollerElt)
        .scrollTop(scrollTo)
        .duration(!localSkipAnimation && 100)
        .start(() => {
          previewFinishTimeoutId = setTimeout(() => {
            isPreviewMoving = false;
          }, 100);
        }, () => {
          isPreviewMoving = true;
        });
    }, localSkipAnimation ? 500 : 50);
  } else if (!store.getters['layout/styles'].showEditor || isScrollPreview) {
    // Scroll the editor
    isScrollPreview = false;
    sectionDescList.some((sectionDesc) => {
      if (previewScrollTop > sectionDesc.previewDimension.endOffset) {
        return false;
      }
      const posInSection = (previewScrollTop - sectionDesc.previewDimension.startOffset)
        / (sectionDesc.previewDimension.height || 1);
      scrollTo = (sectionDesc.editorDimension.startOffset
        + (sectionDesc.editorDimension.height * posInSection));
      return true;
    });
    // If scrollTo is undefined (scrolled past all sections), scroll to bottom
    const maxEditorScroll = editorScrollerElt.scrollHeight - editorScrollerElt.offsetHeight;
    if (scrollTo === undefined) {
      scrollTo = maxEditorScroll;
    } else {
      scrollTo = Math.min(scrollTo, maxEditorScroll);
    }

    throttle(() => {
      clearTimeout(editorFinishTimeoutId);
      animationSvc.animate(editorScrollerElt)
        .scrollTop(scrollTo)
        .duration(!localSkipAnimation && 100)
        .start(() => {
          editorFinishTimeoutId = setTimeout(() => {
            isEditorMoving = false;
          }, 100);
        }, () => {
          isEditorMoving = true;
        });
    }, localSkipAnimation ? 500 : 50);
  }
};

let isPreviewRefreshing;
let timeoutId;

const forceScrollSync = () => {
  if (!isPreviewRefreshing) {
    doScrollSync();
  }
};

// Save current scroll position as section index and position within section
const saveScrollPosition = (useEditor) => {
  if (sectionDescList.length === 0) return;
  const scrollerElt = useEditor ? editorScrollerElt : previewScrollerElt;
  const dimensionKey = useEditor ? 'editorDimension' : 'previewDimension';
  if (!scrollerElt) return;
  const { scrollTop } = scrollerElt;
  sectionDescList.some((sectionDesc, sectionIdx) => {
    if (scrollTop > sectionDesc[dimensionKey].endOffset) {
      return false;
    }
    const posInSection = (scrollTop - sectionDesc[dimensionKey].startOffset) /
      (sectionDesc[dimensionKey].height || 1);
    savedScrollPosition = { sectionIdx, posInSection };
    return true;
  });
};

// Restore scroll position - set preview first, then trigger dimension remeasure and sync
const restoreScrollPosition = () => {
  if (!savedScrollPosition || sectionDescList.length === 0) return;
  const sectionDesc = sectionDescList[savedScrollPosition.sectionIdx];
  if (!sectionDesc) return;
  
  // Set preview scroll position (preview dimensions are always valid)
  const previewScrollTop = sectionDesc.previewDimension.startOffset +
    (sectionDesc.previewDimension.height * savedScrollPosition.posInSection);
  if (previewScrollerElt) {
    previewScrollerElt.scrollTop = Math.floor(previewScrollTop);
  }
  
  // Save position for after remeasure
  const { sectionIdx, posInSection } = savedScrollPosition;
  savedScrollPosition = null;
  
  // Trigger dimension remeasure and wait for it to complete
  // Force remeasure by setting previewCtxMeasured to null
  editorSvc.previewCtxMeasured = null;
  
  // Listen for dimension update and sync editor
  const onMeasured = (previewCtxMeasured) => {
    if (!previewCtxMeasured) return;
    editorSvc.$off('previewCtxMeasured', onMeasured);
    
    const updatedSectionDesc = previewCtxMeasured.sectionDescList[sectionIdx];
    if (updatedSectionDesc && updatedSectionDesc.editorDimension.height > 0) {
      const editorScrollTop = updatedSectionDesc.editorDimension.startOffset +
        (updatedSectionDesc.editorDimension.height * posInSection);
      if (editorScrollerElt) {
        editorScrollerElt.scrollTop = Math.floor(editorScrollTop);
      }
    }
  };
  editorSvc.$on('previewCtxMeasured', onMeasured);
  
  // Trigger the remeasure
  setTimeout(() => {
    editorSvc.measureSectionDimensions(false, true);
  }, 50);
};

store.watch(() => store.getters['data/layoutSettings'].scrollSync, forceScrollSync);

editorSvc.$on('inited', () => {
  editorScrollerElt = editorSvc.editorElt.parentNode;
  previewScrollerElt = editorSvc.previewElt.parentNode;

  editorScrollerElt.addEventListener('scroll', () => {
    if (isEditorMoving) {
      return;
    }
    isScrollEditor = true;
    isScrollPreview = false;
    doScrollSync();
  });

  previewScrollerElt.addEventListener('scroll', () => {
    if (isPreviewMoving || isPreviewRefreshing) {
      return;
    }
    isScrollPreview = true;
    isScrollEditor = false;
    doScrollSync();
  });
});

editorSvc.$on('sectionList', () => {
  clearTimeout(timeoutId);
  isPreviewRefreshing = true;
  sectionDescList = [];
});

editorSvc.$on('previewCtx', () => {
  // Assume the user is writing in the editor
  isScrollEditor = store.getters['layout/styles'].showEditor;
  // A preview scrolling event can occur if height is smaller
  timeoutId = setTimeout(() => {
    isPreviewRefreshing = false;
  }, 100);
});

store.watch(
  () => store.getters['layout/styles'].showEditor,
  (showEditor) => {
    isScrollEditor = showEditor;
    isScrollPreview = !showEditor;
    skipAnimation = true;
    // Note: save/restore is now handled by the toggleEditor action
  },
);

store.watch(
  () => store.getters['file/current'].id,
  () => {
    skipAnimation = true;
  },
);

editorSvc.$on('previewCtxMeasured', (previewCtxMeasured) => {
  if (previewCtxMeasured) {
    ({ sectionDescList } = previewCtxMeasured);
    forceScrollSync();
  }
});

// Expose functions globally for synchronous access from store
window.scrollSyncSave = () => {
  const showEditor = store.getters['layout/styles'].showEditor;
  saveScrollPosition(showEditor);
};

window.scrollSyncRestore = () => {
  restoreScrollPosition();
};
